import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  BookOpen,
  CalendarDays,
  CircleAlert,
  Check,
  ChevronLeft,
  ChevronRight,
  Clock3,
  LogOut,
  Mail,
  Save,
  User,
} from "lucide-react";
import {
  extractTeacherRecord,
  getTeacherEmail,
  getTeacherIdentifier,
  normalizeAvailabilityCollection,
  normalizeTeacherCollection,
  teacherApi,
} from "../../features/teacher/teacherApi";
import {
  clearTeacherSession,
  updateTeacherProfile,
} from "../../features/teacher/teacherSlice";

const availabilityStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Lato:wght@300;400;700;900&display=swap');

  .teacher-availability-page {
    min-height: 100vh;
    background: #faf5ec;
    color: #1f1a17;
    font-family: "Lato", sans-serif;
    font-size: 1rem;
    position: relative;
    overflow-y: auto;
  }

  .teacher-availability-topbar {
    position: sticky;
    top: 0;
    z-index: 10;
    background: rgba(255, 255, 255, 0.95);
    border-bottom: 1px solid #ebe4d8;
    backdrop-filter: blur(14px);
  }

  .teacher-availability-shell {
    width: min(100%, 78rem);
    margin: 0 auto;
    padding-inline: clamp(0.875rem, 2vw, 1.25rem);
  }

  .teacher-availability-topbar-row {
    min-height: clamp(4rem, 5.5vw, 4.5rem);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: clamp(1rem, 2vw, 1.5rem);
  }

  .teacher-availability-brand {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .teacher-availability-brand-badge {
    width: clamp(2.25rem, 2.8vw, 2.45rem);
    height: clamp(2.25rem, 2.8vw, 2.45rem);
    border: 0;
    padding: 0;
    border-radius: 999px;
    display: grid;
    place-items: center;
    background: linear-gradient(135deg, #ff7154 0%, #ff564d 100%);
    color: #ffffff;
    cursor: pointer;
    box-shadow: 0 12px 20px rgba(255, 98, 77, 0.2);
    transition: transform 180ms ease, box-shadow 180ms ease, filter 180ms ease;
  }

  .teacher-availability-brand-badge:hover,
  .teacher-availability-brand-badge:focus-visible {
    outline: none;
    transform: translateY(-1px);
    box-shadow: 0 16px 24px rgba(255, 98, 77, 0.24);
    filter: saturate(1.05);
  }

  .teacher-availability-brand-title {
    margin: 0;
    color: #ff624d;
    font-size: clamp(1.1rem, 1.7vw, 1.35rem);
    font-weight: 700;
  }

  .teacher-availability-actions {
    display: flex;
    align-items: center;
    gap: clamp(0.75rem, 1.5vw, 1rem);
  }

  .teacher-availability-email-pill {
    display: inline-flex;
    align-items: center;
    gap: 0.625rem;
    min-height: 2.5rem;
    padding-inline: 1rem;
    border: 1px solid #dbe3f2;
    border-radius: 999px;
    background: #ffffff;
    color: #6f6a67;
    font-size: 0.9rem;
    white-space: nowrap;
  }

  .teacher-availability-logout {
    display: inline-flex;
    align-items: center;
    gap: 0.625rem;
    border: 0;
    background: transparent;
    color: #1f1a17;
    font: inherit;
    font-size: 0.94rem;
    cursor: pointer;
  }

  .teacher-availability-tabs {
    display: flex;
    align-items: center;
    gap: clamp(0.625rem, 1.5vw, 0.95rem);
    min-height: 3.35rem;
  }

  .teacher-availability-tab {
    min-height: 3.35rem;
    display: inline-flex;
    align-items: center;
    gap: 0.625rem;
    padding-inline: 0.95rem;
    border: 0;
    border-bottom: 0.1875rem solid transparent;
    background: transparent;
    color: #2f2a28;
    font: inherit;
    font-size: 0.95rem;
    cursor: pointer;
  }

  .teacher-availability-tab-active {
    color: #2453d4;
    border-bottom-color: #2453d4;
    background: rgba(226, 235, 255, 0.78);
    border-top-left-radius: 0.875rem;
    border-top-right-radius: 0.875rem;
  }

  .teacher-availability-content {
    padding: clamp(1.25rem, 2.5vw, 1.65rem) clamp(0.875rem, 1.6vw, 1.25rem)
      clamp(2rem, 4vw, 2.75rem);
  }

  .teacher-availability-banner {
    border: 1px solid #d7e3fb;
    border-radius: 1.2rem;
    background:
      radial-gradient(circle at top right, rgba(255, 255, 255, 0.7), transparent 38%),
      linear-gradient(135deg, #e8efff 0%, #dfe8fb 52%, #f7fbff 100%);
    padding: clamp(1.35rem, 2vw, 1.8rem);
    margin-bottom: 1.1rem;
    box-shadow: 0 18px 34px rgba(72, 107, 191, 0.08);
  }

  .teacher-availability-banner h2 {
    margin: 0 0 0.55rem;
    font-size: clamp(1.05rem, 1.5vw, 1.18rem);
    font-weight: 700;
    color: #1f376f;
  }

  .teacher-availability-banner p {
    margin: 0;
    color: #5b6781;
    font-size: 0.94rem;
    line-height: 1.6;
  }

  .teacher-availability-status-stack {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(18rem, 0.98fr);
    gap: 1.5rem;
    margin-bottom: 1.95rem;
  }

  .teacher-availability-message {
    border-radius: 1rem;
    border: 1px solid transparent;
    padding: 1rem 1.1rem;
    font-size: 0.92rem;
    line-height: 1.55;
    box-shadow: 0 12px 24px rgba(70, 93, 140, 0.06);
  }

  .teacher-availability-message-success {
    border-color: #b9efca;
    background: #effcf2;
    color: #1b8b58;
  }

  .teacher-availability-message-error {
    border-color: #ffd1c7;
    background: #fff3f0;
    color: #b34736;
  }

  .teacher-availability-message-info {
    border-color: #d6e3ff;
    background: linear-gradient(135deg, #f3f7ff 0%, #edf4ff 100%);
    color: #315db7;
  }

  .teacher-availability-selected-date {
    margin-bottom: 0;
  }

  .teacher-availability-grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(18rem, 0.98fr);
    gap: 1.5rem;
    align-items: stretch;
  }

  .teacher-availability-card {
    border: 1px solid #dde5ef;
    border-radius: 1rem;
    background: #ffffff;
    padding: clamp(1rem, 1.8vw, 1.3rem);
    height: 100%;
  }

  .teacher-availability-calendar-card,
  .teacher-availability-slots-card {
    display: flex;
    flex-direction: column;
  }

  .teacher-availability-slots-card {
    min-height: 0;
  }

  .teacher-availability-calendar-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 1.5rem;
  }

  .teacher-availability-calendar-title {
    margin: 0;
    color: #ff624d;
    font-size: clamp(0.95rem, 1.3vw, 1.04rem);
    font-weight: 500;
  }

  .teacher-availability-calendar-controls {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .teacher-availability-control {
    width: 2.2rem;
    height: 2.2rem;
    display: grid;
    place-items: center;
    border: 0;
    border-radius: 999px;
    background: transparent;
    color: #9a9a9a;
    cursor: pointer;
  }

  .teacher-availability-weekdays,
  .teacher-availability-days {
    display: grid;
    grid-template-columns: repeat(7, minmax(0, 1fr));
    text-align: center;
  }

  .teacher-availability-weekdays {
    margin-bottom: 0.75rem;
    color: #3a3836;
    font-size: 0.95rem;
    font-weight: 500;
  }

  .teacher-availability-days {
    gap: 0.35rem 0;
  }

  .teacher-availability-day {
    min-height: 5.5rem;
    display: grid;
    place-items: center;
  }

  .teacher-availability-day-empty {
    pointer-events: none;
  }

  .teacher-availability-day-button {
    width: 3.4rem;
    height: 3.4rem;
    display: grid;
    place-items: center;
    border: 0;
    border-radius: 999px;
    background: transparent;
    color: #1f1a17;
    font: inherit;
    font-size: 1rem;
    cursor: pointer;
    transition: background 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;
  }

  .teacher-availability-day-button:hover {
    background: #f5efe5;
  }

  .teacher-availability-day-button:disabled,
  .teacher-availability-day-button:disabled:hover {
    background: #efebe5;
    color: #9b948a;
    cursor: not-allowed;
    box-shadow: none;
  }

  .teacher-availability-day-selected .teacher-availability-day-button {
    width: 3.4rem;
    height: 3.4rem;
    background: #ff624d;
    box-shadow: 0 14px 24px rgba(255, 98, 77, 0.24);
    color: #ffffff;
  }

  .teacher-availability-day-selected .teacher-availability-day-button:disabled {
    background: #d8d1c7;
    color: #7f786f;
    box-shadow: none;
  }

  .teacher-availability-slots-head {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1.5rem;
  }

  .teacher-availability-slots-icon {
    width: 3.4rem;
    height: 3.4rem;
    position: relative;
    border-radius: 999px;
    line-height: 0;
    background: #000000;
    color: #ffffff;
    box-shadow: 0 14px 24px rgba(0, 0, 0, 0.12);
  }

  .teacher-availability-slots-icon svg {
    position: absolute;
    inset: 0;
    margin: auto;
    display: block;
  }

  .teacher-availability-slots-head h2 {
    margin: 0;
    color: #ff624d;
    font-size: clamp(0.95rem, 1.3vw, 1.04rem);
    font-weight: 500;
  }

  .teacher-availability-slot-list {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    gap: 0.75rem;
    max-height: clamp(24rem, 52vh, 34rem);
    overflow-y: auto;
    padding-right: 0.35rem;
  }

  .teacher-availability-slot-list::-webkit-scrollbar {
    width: 0.45rem;
  }

  .teacher-availability-slot-list::-webkit-scrollbar-thumb {
    background: #d2dbef;
    border-radius: 999px;
  }

  .teacher-availability-slot-list::-webkit-scrollbar-track {
    background: transparent;
  }

  .teacher-availability-slot {
    width: 100%;
    min-height: 4rem;
    padding: 0.75rem 1.25rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    border: 1px solid #dde5ef;
    border-radius: 0.85rem;
    background: #ffffff;
    color: #1f1a17;
    font: inherit;
    cursor: pointer;
    transition: border-color 0.2s ease, background 0.2s ease,
      box-shadow 0.2s ease, color 0.2s ease;
  }

  .teacher-availability-slot:hover {
    border-color: #c9d8fb;
  }

  .teacher-availability-slot-active {
    border-color: #2d61f0;
    background: #f5f8ff;
    box-shadow: 0 10px 22px rgba(45, 97, 240, 0.12);
    color: #2348a8;
  }

  .teacher-availability-slot-saved {
    border-color: #1b8b58;
    background: linear-gradient(135deg, #effcf2 0%, #e2f8ea 100%);
    box-shadow: 0 10px 22px rgba(27, 139, 88, 0.12);
    color: #176d47;
  }

  .teacher-availability-slot-past,
  .teacher-availability-slot-past:hover {
    border-color: #d8dde7;
    background: #f1f3f6;
    box-shadow: none;
    color: #8a8f98;
  }

  .teacher-availability-slot:disabled {
    cursor: not-allowed;
    opacity: 1;
  }

  .teacher-availability-slot-label {
    font-size: 0.98rem;
    font-weight: 500;
  }

  .teacher-availability-slot-badge {
    min-height: 2.2rem;
    padding-inline: 0.95rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.45rem;
    border-radius: 999px;
    background: #2d61f0;
    color: #ffffff;
    font-size: 0.95rem;
    font-weight: 500;
    white-space: nowrap;
  }

  .teacher-availability-slot-badge-saved {
    background: #1b8b58;
  }

  .teacher-availability-save {
    width: 100%;
    min-height: 3.4rem;
    margin-top: 1.25rem;
    border: 0;
    border-radius: 0.85rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    background: linear-gradient(135deg, #ff7154 0%, #ff564d 100%);
    box-shadow: 0 16px 26px rgba(255, 98, 77, 0.2);
    color: #ffffff;
    font: inherit;
    font-size: 0.98rem;
    font-weight: 700;
    cursor: pointer;
  }

  .teacher-availability-save:disabled {
    cursor: not-allowed;
    opacity: 0.75;
  }

  .teacher-availability-save-feedback {
    width: 100%;
    min-height: 4.55rem;
    margin-top: 1.1rem;
    padding: 1rem 1.25rem;
    border-radius: 1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.85rem;
    box-shadow: 0 16px 28px rgba(31, 26, 23, 0.1);
    font-size: 1rem;
    font-weight: 600;
    text-align: center;
    box-sizing: border-box;
    animation: teacher-availability-popup-in 0.22s ease-out;
  }

  .teacher-availability-save-feedback-success {
    border: 1px solid #b7efd3;
    background: linear-gradient(135deg, #dff8e5 0%, #cff5dd 50%, #c9f6e8 100%);
    color: #0f8f56;
  }

  .teacher-availability-save-feedback-error {
    border: 1px solid #ffd1c7;
    background: linear-gradient(135deg, #fff6f3 0%, #ffe8e1 100%);
    color: #b34736;
  }

  .teacher-availability-save-feedback svg {
    flex: 0 0 auto;
  }

  @keyframes teacher-availability-popup-in {
    from {
      opacity: 0;
      transform: translate3d(0, 0.5rem, 0);
    }

    to {
      opacity: 1;
      transform: translate3d(0, 0, 0);
    }
  }

  @media (max-width: 980px) {
    .teacher-availability-grid {
      grid-template-columns: 1fr;
    }

    .teacher-availability-status-stack {
      grid-template-columns: 1fr;
    }
  }

  @media (max-width: 768px) {
    .teacher-availability-save-feedback {
      min-height: 4.15rem;
      padding: 0.95rem 1rem;
      font-size: 0.95rem;
    }

    .teacher-availability-topbar-row {
      min-height: auto;
      padding: 1.125rem 0;
      flex-direction: column;
      align-items: flex-start;
    }

    .teacher-availability-actions {
      width: 100%;
      flex-direction: column;
      align-items: stretch;
    }

    .teacher-availability-email-pill,
    .teacher-availability-logout {
      justify-content: center;
    }

    .teacher-availability-tabs {
      overflow-x: auto;
      padding-bottom: 0.5rem;
    }

    .teacher-availability-tab {
      white-space: nowrap;
    }
  }
`;

const weekdays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const AVAILABILITY_TIME_ZONE_TEXT =
  "All availability times on this page follow Australian Eastern Time (AEST).";
const AVAILABILITY_TIME_ZONE = "Australia/Sydney";
const SAVED_AVAILABILITY_STORAGE_PREFIX = "teacherSavedAvailabilitySlots";
const timeSlots = Array.from({ length: 12 }, (_, index) => {
  const startHour = index + 9;
  const endHour = startHour + 1;

  return `${String(startHour).padStart(2, "0")}:00 - ${String(endHour).padStart(
    2,
    "0"
  )}:00`;
});

const formatDateKey = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate()
  ).padStart(2, "0")}`;

const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();

const getCalendarCells = (monthDate) => {
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const firstWeekday = new Date(year, month, 1).getDay();
  const totalDays = getDaysInMonth(year, month);

  return [
    ...Array.from({ length: firstWeekday }, (_, index) => ({
      type: "blank",
      id: `blank-${index}`,
    })),
    ...Array.from({ length: totalDays }, (_, index) => ({
      type: "day",
      value: index + 1,
    })),
  ];
};

const buildAvailabilityPayload = (dateKey, slot) => {
  const [startTime = "", endTime = ""] = String(slot).split(" - ");

  return {
    date: dateKey,
    start_time: startTime.trim(),
    end_time: endTime.trim(),
    slot_duration: 60,
  };
};

const toAvailabilityDateParts = (value) => {
  if (!value) return null;

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: AVAILABILITY_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });

  return formatter
    .formatToParts(date)
    .reduce((parts, part) => {
      if (part.type !== "literal") {
        parts[part.type] = part.value;
      }
      return parts;
    }, {});
};

const toAvailabilityTimeParts = (value) => {
  if (!value) return null;

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  const formatter = new Intl.DateTimeFormat("en-GB", {
    timeZone: AVAILABILITY_TIME_ZONE,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

  return formatter
    .formatToParts(date)
    .reduce((parts, part) => {
      if (part.type !== "literal") {
        parts[part.type] = part.value;
      }
      return parts;
    }, {});
};

const normalizeAvailabilityDateKey = (value) => {
  const text = String(value || "").trim();
  if (!text) return "";

  const matchedDate = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (matchedDate) {
    return `${matchedDate[1]}-${matchedDate[2]}-${matchedDate[3]}`;
  }

  const parts = toAvailabilityDateParts(text);
  if (parts?.year && parts?.month && parts?.day) {
    return `${parts.year}-${parts.month}-${parts.day}`;
  }

  return "";
};

const normalizeAvailabilityTimePart = (value) => {
  const text = String(value || "").trim();
  if (!text) return "";

  const matchedShortTime = text.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (matchedShortTime) {
    return `${matchedShortTime[1].padStart(2, "0")}:${matchedShortTime[2]}`;
  }

  const matchedMeridiem = text.match(/^(\d{1,2}):(\d{2})\s*([AP]M)$/i);
  if (matchedMeridiem) {
    let hour = Number(matchedMeridiem[1]) % 12;
    if (matchedMeridiem[3].toUpperCase() === "PM") {
      hour += 12;
    }

    return `${String(hour).padStart(2, "0")}:${matchedMeridiem[2]}`;
  }

  const parts = toAvailabilityTimeParts(text);
  if (parts?.hour && parts?.minute) {
    return `${parts.hour}:${parts.minute}`;
  }

  return "";
};

const getAvailabilityCurrentDateKey = (value = new Date()) => {
  const parts = toAvailabilityDateParts(value);
  if (!parts?.year || !parts?.month || !parts?.day) return "";
  return `${parts.year}-${parts.month}-${parts.day}`;
};

const isPastAvailabilityDate = (dateKey, now = new Date()) => {
  const normalizedDateKey = normalizeAvailabilityDateKey(dateKey);
  const currentDateKey = getAvailabilityCurrentDateKey(now);

  if (!normalizedDateKey || !currentDateKey) {
    return false;
  }

  return normalizedDateKey < currentDateKey;
};

const getAvailabilityCurrentTimeKey = (value = new Date()) => {
  const parts = toAvailabilityTimeParts(value);
  if (!parts?.hour || !parts?.minute) return "";
  return `${parts.hour}:${parts.minute}`;
};

const convertAvailabilityTimeToMinutes = (value) => {
  const normalizedValue = normalizeAvailabilityTimePart(value);
  if (!normalizedValue) return -1;

  const [hours = "0", minutes = "0"] = normalizedValue.split(":");
  return Number(hours) * 60 + Number(minutes);
};

const extractAvailabilityTimes = (slot) => {
  if (!slot || typeof slot !== "object") return { start: "", end: "" };

  const start =
    normalizeAvailabilityTimePart(
      slot.start_time ||
        slot.startTime ||
        slot.start ||
        slot.from ||
        slot.begin ||
        slot.slot_start
    ) || "";
  const end =
    normalizeAvailabilityTimePart(
      slot.end_time ||
        slot.endTime ||
        slot.end ||
        slot.to ||
        slot.finish ||
        slot.slot_end
    ) || "";

  if (start && end) {
    return { start, end };
  }

  const label = String(slot.label || slot.slot || slot.time || "").trim();
  if (label.includes(" - ")) {
    const [rangeStart = "", rangeEnd = ""] = label.split(" - ");
    return {
      start: normalizeAvailabilityTimePart(rangeStart),
      end: normalizeAvailabilityTimePart(rangeEnd),
    };
  }

  return { start: "", end: "" };
};

const getAvailabilitySlotKey = (slot) => {
  const [start = "", end = ""] = String(slot || "").split(" - ");
  const normalizedStart = normalizeAvailabilityTimePart(start);
  const normalizedEnd = normalizeAvailabilityTimePart(end);

  if (!normalizedStart || !normalizedEnd) return "";
  return `${normalizedStart} - ${normalizedEnd}`;
};

const isPastAvailabilitySlot = (dateKey, slot, now = new Date()) => {
  const normalizedDateKey = normalizeAvailabilityDateKey(dateKey);
  const currentDateKey = getAvailabilityCurrentDateKey(now);

  if (!normalizedDateKey || !currentDateKey) {
    return false;
  }

  if (isPastAvailabilityDate(normalizedDateKey, now)) {
    return true;
  }

  if (normalizedDateKey > currentDateKey) {
    return false;
  }

  const [slotStart = ""] = getAvailabilitySlotKey(slot).split(" - ");
  const slotStartMinutes = convertAvailabilityTimeToMinutes(slotStart);
  const currentTimeMinutes = convertAvailabilityTimeToMinutes(
    getAvailabilityCurrentTimeKey(now)
  );

  if (slotStartMinutes < 0 || currentTimeMinutes < 0) {
    return false;
  }

  return slotStartMinutes <= currentTimeMinutes;
};

const mergeSavedAvailabilityMaps = (...maps) =>
  maps.reduce((mergedMap, currentMap) => {
    Object.entries(currentMap || {}).forEach(([dateKey, slots]) => {
      const mergedSlots = new Set(mergedMap[dateKey] || []);
      (slots || []).forEach((slot) => {
        const slotKey = getAvailabilitySlotKey(slot);
        if (slotKey) {
          mergedSlots.add(slotKey);
        }
      });

      mergedMap[dateKey] = Array.from(mergedSlots);
    });

    return mergedMap;
  }, {});

const readSavedAvailabilityCache = (teacherId) => {
  if (!teacherId || typeof window === "undefined") return {};

  try {
    const rawValue = window.localStorage.getItem(
      `${SAVED_AVAILABILITY_STORAGE_PREFIX}:${teacherId}`
    );
    const parsedValue = JSON.parse(rawValue || "{}");
    return parsedValue && typeof parsedValue === "object" ? parsedValue : {};
  } catch (_error) {
    return {};
  }
};

const writeSavedAvailabilityCache = (teacherId, availabilityMap) => {
  if (!teacherId || typeof window === "undefined") return;

  try {
    window.localStorage.setItem(
      `${SAVED_AVAILABILITY_STORAGE_PREFIX}:${teacherId}`,
      JSON.stringify(availabilityMap || {})
    );
  } catch (_error) {
    // Ignore cache write failures.
  }
};

const buildSavedAvailabilityMap = (payload) =>
  normalizeAvailabilityCollection(payload).reduce((map, item) => {
    const dateKey = normalizeAvailabilityDateKey(
      item?.date ||
        item?.available_date ||
        item?.day ||
        item?.slot_date ||
        item?.start_time ||
        item?.start
    );
    const { start, end } = extractAvailabilityTimes(item);

    if (!dateKey || !start || !end) {
      return map;
    }

    const slotKey = `${start} - ${end}`;
    const nextSlots = new Set(map[dateKey] || []);
    nextSlots.add(slotKey);
    map[dateKey] = Array.from(nextSlots);
    return map;
  }, {});

const extractConflictingAvailabilityMap = (failures) =>
  (failures || []).reduce((map, failure) => {
    const conflictingSlots = failure?.error?.raw?.conflicting_slots;

    if (!Array.isArray(conflictingSlots)) {
      return map;
    }

    conflictingSlots.forEach((item) => {
      const dateKey = normalizeAvailabilityDateKey(
        item?.date || item?.start_time || item?.start
      );
      const start = normalizeAvailabilityTimePart(
        item?.start_time || item?.start
      );
      const end = normalizeAvailabilityTimePart(item?.end_time || item?.end);

      if (!dateKey || !start || !end) {
        return;
      }

      const nextSlots = new Set(map[dateKey] || []);
      nextSlots.add(`${start} - ${end}`);
      map[dateKey] = Array.from(nextSlots);
    });

    return map;
  }, {});

export default function TeacherAvailability() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const teacherSession = useSelector((state) => state.teacher?.teacher);
  const today = useMemo(() => new Date(), []);
  const [currentMonth, setCurrentMonth] = useState(
    new Date(today.getFullYear(), today.getMonth(), 1)
  );
  const [selectedDate, setSelectedDate] = useState(today);
  const [availabilityByDate, setAvailabilityByDate] = useState({});
  const [savedAvailabilityOverrides, setSavedAvailabilityOverrides] = useState({});
  const [saveFeedback, setSaveFeedback] = useState(null);
  const [currentTime, setCurrentTime] = useState(() => new Date());

  const profileQuery = useQuery({
    queryKey: ["teacher-availability-profile"],
    queryFn: teacherApi.getProfile,
    retry: false,
  });

  useEffect(() => {
    const teacherRecord = extractTeacherRecord(profileQuery.data);
    if (teacherRecord) {
      dispatch(updateTeacherProfile(teacherRecord));
    }
  }, [dispatch, profileQuery.data]);

  useEffect(() => {
    if (!saveFeedback) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setSaveFeedback(null);
    }, 3500);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [saveFeedback]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setCurrentTime(new Date());
    }, 30000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, []);

  const teacherProfile =
    extractTeacherRecord(profileQuery.data, teacherSession || {}) || teacherSession || {};
  const teacherEmail = getTeacherEmail(teacherProfile);
  const teacherId = getTeacherIdentifier(teacherProfile);
  const normalizedTeacherEmail = String(teacherEmail || "").trim().toLowerCase();

  const tutorsQuery = useQuery({
    queryKey: ["teacher-availability-tutors"],
    queryFn: teacherApi.getAllTeachers,
    enabled: Boolean(normalizedTeacherEmail),
    retry: false,
  });

  const availabilityTutorId = useMemo(() => {
    const teachers = normalizeTeacherCollection(tutorsQuery.data);

    const matchedTeacher = teachers.find((teacher) => {
      const candidateEmail = getTeacherEmail(teacher).toLowerCase();
      return candidateEmail && candidateEmail === normalizedTeacherEmail;
    });

    return getTeacherIdentifier(matchedTeacher) || teacherId;
  }, [normalizedTeacherEmail, teacherId, tutorsQuery.data]);
  const savedAvailabilityOwnerKey =
    availabilityTutorId || teacherId || normalizedTeacherEmail;

  const existingAvailabilityQuery = useQuery({
    queryKey: ["teacher-availability-existing", availabilityTutorId],
    queryFn: () => teacherApi.getTutorAvailability(availabilityTutorId),
    enabled: Boolean(availabilityTutorId),
    retry: false,
  });

  useEffect(() => {
    setSavedAvailabilityOverrides(readSavedAvailabilityCache(savedAvailabilityOwnerKey));
  }, [savedAvailabilityOwnerKey]);

  useEffect(() => {
    if (!savedAvailabilityOwnerKey || !existingAvailabilityQuery.data) {
      return;
    }

    const backendSavedAvailability = buildSavedAvailabilityMap(
      existingAvailabilityQuery.data
    );

    setSavedAvailabilityOverrides((previous) => {
      const mergedMap = mergeSavedAvailabilityMaps(
        previous,
        backendSavedAvailability
      );
      writeSavedAvailabilityCache(savedAvailabilityOwnerKey, mergedMap);
      return mergedMap;
    });
  }, [existingAvailabilityQuery.data, savedAvailabilityOwnerKey]);

  const selectedDateKey = formatDateKey(selectedDate);
  const selectedSlots = availabilityByDate[selectedDateKey] || [];
  const savedAvailabilityByDate = useMemo(
    () =>
      mergeSavedAvailabilityMaps(
        buildSavedAvailabilityMap(existingAvailabilityQuery.data),
        savedAvailabilityOverrides
      ),
    [existingAvailabilityQuery.data, savedAvailabilityOverrides]
  );
  const savedSlots = savedAvailabilityByDate[selectedDateKey] || [];
  const calendarCells = getCalendarCells(currentMonth);
  const currentMonthLabel = currentMonth.toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });

  const saveAvailabilityMutation = useMutation({
    mutationFn: async ({ dateKey, slots }) => {
      const successes = [];
      const failures = [];

      for (const slot of slots) {
        try {
          const response = await teacherApi.createAvailability(
            buildAvailabilityPayload(dateKey, slot)
          );
          successes.push({ slot, response });
        } catch (error) {
          failures.push({ slot, error });
        }
      }

      return { successes, failures };
    },
    onSuccess: ({ successes, failures }, variables) => {
      const successfulMap = {
        [variables.dateKey]: successes
          .map(({ slot }) => getAvailabilitySlotKey(slot))
          .filter(Boolean),
      };
      const conflictingMap = extractConflictingAvailabilityMap(failures);
      const mergedMap = mergeSavedAvailabilityMaps(
        savedAvailabilityOverrides,
        successfulMap,
        conflictingMap
      );

      setSavedAvailabilityOverrides(mergedMap);
      writeSavedAvailabilityCache(savedAvailabilityOwnerKey, mergedMap);

      const lockedSlots = new Set([
        ...Object.values(successfulMap).flat(),
        ...Object.values(conflictingMap).flat(),
      ]);

      setAvailabilityByDate((previous) => {
        const existingSlots = previous[variables.dateKey] || [];
        const remainingSlots = existingSlots.filter(
          (slot) => !lockedSlots.has(getAvailabilitySlotKey(slot))
        );

        return {
          ...previous,
          [variables.dateKey]: remainingSlots,
        };
      });

      if (successes.length > 0) {
        setSaveFeedback({
          type: "success",
          message: "Availability saved successfully!",
        });
      } else if (failures.length > 0) {
        setSaveFeedback({
          type: "error",
          message:
            failures[0]?.error?.message ||
            "Unable to save availability for the selected slots.",
        });
      } else {
        setSaveFeedback(null);
      }

      existingAvailabilityQuery.refetch();
    },
  });

  const handleLogout = () => {
    dispatch(clearTeacherSession());
    navigate("/teacherlogin", { replace: true });
  };

  const handleDashboardNavigation = () => {
    navigate("/teacherdashboard");
  };

  const changeMonth = (offset) => {
    const nextMonth = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth() + offset,
      1
    );
    const nextDay = Math.min(
      selectedDate.getDate(),
      getDaysInMonth(nextMonth.getFullYear(), nextMonth.getMonth())
    );

    setCurrentMonth(nextMonth);
    setSelectedDate(
      new Date(nextMonth.getFullYear(), nextMonth.getMonth(), nextDay)
    );
  };

  const handleDateSelect = (day) => {
    const nextDate = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      day
    );
    if (isPastAvailabilityDate(formatDateKey(nextDate), currentTime)) {
      return;
    }

    setSaveFeedback(null);
    setSelectedDate(nextDate);
  };

  const toggleSlot = (slot) => {
    const slotKey = getAvailabilitySlotKey(slot);
    if (savedSlots.includes(slotKey)) {
      return;
    }

    setSaveFeedback(null);
    setAvailabilityByDate((previous) => {
      const existingSlots = previous[selectedDateKey] || [];
      const nextSlots = existingSlots.includes(slot)
        ? existingSlots.filter((item) => item !== slot)
        : [...existingSlots, slot];

      return {
        ...previous,
        [selectedDateKey]: nextSlots,
      };
    });
  };

  const handleSave = () => {
    if (!selectedSlots.length) {
      return;
    }

    setSaveFeedback(null);
    saveAvailabilityMutation.mutate({
      dateKey: selectedDateKey,
      slots: selectedSlots,
    });
  };

  return (
    <>
      <style>{availabilityStyles}</style>
      <main className="teacher-availability-page">
        <header className="teacher-availability-topbar">
          <div className="teacher-availability-shell">
            <div className="teacher-availability-topbar-row">
              <div className="teacher-availability-brand">
                <button
                  className="teacher-availability-brand-badge"
                  type="button"
                  aria-label="Go to teacher dashboard"
                  onClick={handleDashboardNavigation}
                >
                  <BookOpen size={20} />
                </button>
                <h1 className="teacher-availability-brand-title">
                  Teacher Dashboard
                </h1>
              </div>

              <div className="teacher-availability-actions">
                <div className="teacher-availability-email-pill">
                  <Mail size={18} />
                  {teacherEmail || "Teacher account"}
                </div>
                <button
                  className="teacher-availability-logout"
                  type="button"
                  onClick={handleLogout}
                >
                  <LogOut size={18} />
                  Logout
                </button>
              </div>
            </div>
          </div>
        </header>

        <div className="teacher-availability-shell">
          <nav className="teacher-availability-tabs" aria-label="Teacher tabs">
            <button
              className="teacher-availability-tab"
              type="button"
              onClick={() => navigate("/teacherdashboard")}
            >
              <BookOpen size={20} />
              Bookings
            </button>
            <button
              className="teacher-availability-tab teacher-availability-tab-active"
              type="button"
            >
              <CalendarDays size={20} />
              Availability
            </button>
            <button
              className="teacher-availability-tab"
              type="button"
              onClick={() => navigate("/teacherprofile")}
            >
              <User size={20} />
              Profile
            </button>
          </nav>
        </div>

        <div className="teacher-availability-shell teacher-availability-content">
          <section className="teacher-availability-banner">
            <h2>Manage Your Availability</h2>
            <p>
              Select the time slots when you're available. Each selected slot represents a 60-minute session, will be saved on selecting and saving the availability.

            </p>
          </section>

          {profileQuery.isError ? (
            <div className="teacher-availability-message teacher-availability-message-error" style={{ marginBottom: '1rem' }}>
              {profileQuery.error.message}
            </div>
          ) : null}
          <div className="teacher-availability-status-stack">
            <div className="teacher-availability-message teacher-availability-message-info teacher-availability-selected-date">
              Selected date: {selectedDateKey}
            </div>
            <div className="teacher-availability-message teacher-availability-message-info">
              {AVAILABILITY_TIME_ZONE_TEXT}
            </div>
          </div>

          <section className="teacher-availability-grid">
            <article className="teacher-availability-card teacher-availability-calendar-card">
              <div className="teacher-availability-calendar-header">
                <h2 className="teacher-availability-calendar-title">{currentMonthLabel}</h2>
                <div className="teacher-availability-calendar-controls">
                  <button
                    className="teacher-availability-control"
                    type="button"
                    onClick={() => changeMonth(-1)}
                    aria-label="Previous month"
                  >
                    <ChevronLeft size={20} />
                  </button>
                  <button
                    className="teacher-availability-control"
                    type="button"
                    onClick={() => changeMonth(1)}
                    aria-label="Next month"
                  >
                    <ChevronRight size={20} />
                  </button>
                </div>
              </div>

              <div className="teacher-availability-weekdays">
                {weekdays.map((day) => (
                  <span key={day}>{day}</span>
                ))}
              </div>

              <div className="teacher-availability-days">
                {calendarCells.map((day, index) => {
                  if (day.type === "blank") {
                    return (
                      <div
                        key={day.id || `blank-${index}`}
                        className="teacher-availability-day teacher-availability-day-empty"
                      />
                    );
                  }

                  const isSelected =
                    selectedDate.getFullYear() === currentMonth.getFullYear() &&
                    selectedDate.getMonth() === currentMonth.getMonth() &&
                    selectedDate.getDate() === day.value;
                  const dayDateKey = formatDateKey(
                    new Date(
                      currentMonth.getFullYear(),
                      currentMonth.getMonth(),
                      day.value
                    )
                  );
                  const isPastDate = isPastAvailabilityDate(
                    dayDateKey,
                    currentTime
                  );

                  return (
                    <div
                      key={day.value}
                      className={`teacher-availability-day${
                        isSelected ? " teacher-availability-day-selected" : ""
                      }`}
                    >
                      <button
                        className="teacher-availability-day-button"
                        type="button"
                        onClick={() => handleDateSelect(day.value)}
                        aria-pressed={isSelected}
                        disabled={isPastDate}
                        aria-label={`Select ${currentMonthLabel} ${day.value}`}
                      >
                        {day.value}
                      </button>
                    </div>
                  );
                })}
              </div>
            </article>

            <article className="teacher-availability-card teacher-availability-slots-card">
              <div className="teacher-availability-slots-head">
                <div className="teacher-availability-slots-icon" aria-hidden="true">
                  <Clock3 size={22} />
                </div>
                <h2>Available Time Slots</h2>
              </div>

              <div className="teacher-availability-slot-list">
                {timeSlots.map((slot) => {
                  const slotKey = getAvailabilitySlotKey(slot);
                  const isSaved = savedSlots.includes(slotKey);
                  const isSelected = selectedSlots.includes(slot);
                  const isPast = isPastAvailabilitySlot(
                    selectedDateKey,
                    slot,
                    currentTime
                  );
                  const isHighlighted = !isPast && (isSaved || isSelected);

                  return (
                    <button
                      key={slot}
                      className={`teacher-availability-slot${
                        isHighlighted ? " teacher-availability-slot-active" : ""
                      }${
                        isSaved ? " teacher-availability-slot-saved" : ""
                      }${
                        isPast ? " teacher-availability-slot-past" : ""
                      }`}
                      type="button"
                      onClick={() => toggleSlot(slot)}
                      aria-pressed={isHighlighted}
                      disabled={isSaved || isPast}
                    >
                      <span className="teacher-availability-slot-label">{slot}</span>
                      {isHighlighted ? (
                        <span
                          className={`teacher-availability-slot-badge${
                            isSaved ? " teacher-availability-slot-badge-saved" : ""
                          }`}
                        >
                          <Check size={16} />
                          {isSaved ? "Saved" : "Selected"}
                        </span>
                      ) : null}
                    </button>
                  );
                })}
              </div>

              <button
                className="teacher-availability-save"
                type="button"
                onClick={handleSave}
                disabled={saveAvailabilityMutation.isPending || !selectedSlots.length}
              >
                <Save size={18} />
                {saveAvailabilityMutation.isPending ? "Saving..." : "Save Availability"}
              </button>

              {saveFeedback ? (
                <div
                  className={`teacher-availability-save-feedback ${
                    saveFeedback.type === "error"
                      ? "teacher-availability-save-feedback-error"
                      : "teacher-availability-save-feedback-success"
                  }`}
                  role="status"
                  aria-live="polite"
                >
                  {saveFeedback.type === "error" ? (
                    <CircleAlert size={20} />
                  ) : (
                    <Check size={20} />
                  )}
                  <span>{saveFeedback.message}</span>
                </div>
              ) : null}

            </article>
          </section>
        </div>
      </main>
    </>
  );
}
